<div class="h-full w-full p-6 @lg:p-10 flex flex-col gap-6 pb-32 min-h-full bg-body-background @container">

  <header class="flex flex-col @md:flex-row justify-between items-start @md:items-center gap-4 shrink-0">
    <div class="flex flex-col w-full gap-2">
      <div class="flex items-center justify-between gap-3">
        <h1 class="text-3xl @lg:text-4xl font-extrabold text-heading tracking-tight m-0">{{ config.texts.pageTitle }}
        </h1>
        <button nz-button
                nzType="default"
                nzShape="circle"
                nz-tooltip
                [nzTooltipTitle]="config.texts.aiContextBtnTooltip"
                (click)="openAiContextModal()"
                class="text-secondary-text hover:text-primary transition-colors focus:outline-none flex items-center justify-center border-secondary-border shadow-sm bg-component-inner">
          <i class="fa-light fa-file-lines"></i>
        </button>
      </div>
      <p class="text-secondary-text text-lg m-0">{{ config.texts.pageSubtitle }}</p>
    </div>
  </header>

  <div
       class="flex flex-col @md:flex-row gap-4 justify-between items-center bg-component-inner border border-primary-border rounded-xl p-4 shadow-sm shrink-0">
    <nz-input-group [nzPrefix]="searchIcon"
                    class="w-full @md:w-1/2">
      <input type="text"
             nz-input
             [placeholder]="config.texts.searchPlaceholder"
             [ngModel]="searchQuery()"
             (ngModelChange)="searchQuery.set($event)"
             class="bg-input-background text-primary-text border-secondary-border focus:ring-focus-ring" />
    </nz-input-group>
    <ng-template #searchIcon><i class="fas fa-search text-secondary-text"></i></ng-template>

    <div class="flex items-center w-full @md:w-auto">
      <button (click)="toggleAllGroups()"
              class="w-full text-sm font-semibold px-4 py-2 rounded-lg bg-alt-background border border-secondary-border text-secondary-text hover:text-primary hover:border-primary transition-colors flex items-center justify-center gap-2 shadow-sm whitespace-nowrap">
        <i class="fas"
           [ngClass]="areAllGroupsExpanded() ? 'fa-compress-alt' : 'fa-expand-alt'"></i>
        {{ areAllGroupsExpanded() ? config.texts.collapseAll : config.texts.expandAll }}
      </button>
    </div>
  </div>

  <div class="flex flex-wrap gap-2 shrink-0">
    <nz-tag [nzColor]="selectedDomain() === null ? 'blue' : 'default'"
            class="cursor-pointer !m-0 text-sm px-4 py-1.5 rounded-full transition-colors"
            [ngClass]="{'bg-section-background border-secondary-border': selectedDomain() !== null}"
            (click)="selectDomain(null)">
      {{ config.texts.allDomains }}
    </nz-tag>

    @for (domain of uniqueDomains(); track domain.key) {
    <nz-tag [nzColor]="selectedDomain() === domain.key ? 'blue' : 'default'"
            class="cursor-pointer !m-0 text-sm px-4 py-1.5 rounded-full transition-colors"
            [ngClass]="{'bg-section-background border-secondary-border': selectedDomain() !== domain.key}"
            (click)="selectDomain(domain.key)">
      {{ domain.name }}
    </nz-tag>
    }
  </div>

  @if (isLoading()) {
  <div class="flex items-center justify-center p-20 w-full flex-1">
    <i class="fas fa-circle-notch fa-spin text-5xl text-primary"></i>
  </div>
  } @else if (filteredGroupedModels().length === 0) {
  <div
       class="text-center p-12 bg-component-inner border border-primary-border rounded-xl shadow-sm w-full max-w-2xl mx-auto mt-8">
    <i class="fad fa-database text-5xl text-secondary-text mb-4"></i>
    <p class="text-primary-text font-medium">{{ config.texts.emptyState }}</p>
  </div>
  } @else {
  <div class="flex flex-col gap-10 max-w-full w-full mx-auto mt-2 mb-12">
    @for (group of filteredGroupedModels(); track group.domainKey) {
    <section class="flex flex-col gap-4">

      <button class="flex items-center justify-between px-2 mb-2 w-full group/header text-left"
              (click)="toggleGroup(group.domainKey)"
              (keydown.enter)="toggleGroup(group.domainKey)"
              (keydown.space)="$event.preventDefault(); toggleGroup(group.domainKey)">
        <div class="flex items-center gap-3">
          <h2 class="text-2xl font-bold text-heading group-hover/header:text-primary transition-colors">{{
            group.domainName }}</h2>
          <nz-tag class="!m-0 text-sm bg-alt-bold-background border-secondary-border text-secondary-text">{{
            group.domainKey }}</nz-tag>
          <span
                class="text-xs font-bold text-secondary-text bg-component-inner border border-primary-border px-2.5 py-0.5 rounded-full shadow-sm">{{
            group.models.length }}</span>
        </div>
        <div
             class="text-secondary-text hover:text-primary transition-colors w-8 h-8 rounded hover:bg-alt-background flex items-center justify-center flex-shrink-0">
          <i class="fas"
             [ngClass]="expandedGroups()[group.domainKey] !== false ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>
      </button>

      @if (expandedGroups()[group.domainKey] !== false) {
      <div class="grid grid-cols-1 @md:grid-cols-1 @lg:grid-cols-2 @xl:grid-cols-2 gap-4">
        @for (model of group.models; track model.id) {
        <button (click)="goToDetails(model.id)"
                class="w-full text-left bg-component-inner border border-primary-border rounded-xl shadow-sm flex flex-col transition-all duration-300 hover:border-primary hover:shadow-default focus:outline-none focus:ring-2 focus:ring-focus-ring group">

          <div class="p-5 flex flex-col w-full bg-transparent rounded-xl">
            <div class="flex items-start gap-4 w-full">
              <div
                   class="w-10 h-10 bg-alt-background border border-secondary-border rounded-lg flex items-center justify-center text-primary shrink-0 group-hover:scale-105 group-hover:bg-primary/10 group-hover:border-primary/30 transition-all mt-1">
                <i class="fad fa-table text-lg"></i>
              </div>

              <div class="flex flex-col flex-1 min-w-0 justify-center">
                <div class="flex items-center gap-3 mb-1">
                  <h3 class="text-lg font-bold text-primary-text group-hover:text-primary transition-colors truncate">{{
                    model.title }}</h3>
                  <nz-tag
                          class="!m-0 text-xs bg-alt-bold-background border-secondary-border text-secondary-text font-mono truncate">{{
                    model.dataTableName || model.name }}</nz-tag>
                </div>
                <p class="text-sm text-secondary-text line-clamp-2 leading-relaxed">
                  {{ model.description || config.texts.noDescription }}
                </p>
              </div>

              <div
                   class="flex items-center justify-center self-center shrink-0 w-8 h-8 opacity-0 group-hover:opacity-100 text-primary transition-all duration-300 transform -translate-x-3 group-hover:translate-x-0">
                <i class="fas fa-arrow-right text-xl"></i>
              </div>
            </div>
          </div>

        </button>
        }
      </div>
      }

    </section>
    }
  </div>
  }

  <nz-modal [(nzVisible)]="isAiContextModalVisible"
            [nzTitle]="config.texts.aiContextModalTitle"
            [nzWidth]="800"
            (nzOnCancel)="closeAiContextModal()"
            [nzFooter]="aiContextModalFooter">
    <ng-container *nzModalContent>
      @if (isAiContextLoading()) {
      <div class="flex flex-col items-center justify-center p-12 h-64 gap-4">
        <i class="fas fa-circle-notch fa-spin text-4xl text-primary"></i>
        <span class="text-secondary-text font-medium"
              i18n="@@database.aiContextLoading">{{ config.texts.aiContextLoading }}</span>
      </div>
      } @else {
      <div
           class="w-full max-h-[60vh] overflow-y-auto custom-scrollbar bg-alt-bold-background border border-secondary-border rounded-lg p-4 shadow-inner">
        <pre class="whitespace-pre-wrap font-mono text-sm text-primary-text m-0">{{ aiContextContent() }}</pre>
      </div>
      }
    </ng-container>
    <ng-template #aiContextModalFooter>
      <div class="flex justify-end gap-3 mt-2">
        <button nz-button
                nzType="default"
                (click)="closeAiContextModal()"
                class="border-secondary-border text-secondary-text hover:text-primary transition-colors font-semibold">
          {{ config.texts.aiContextModalClose }}
        </button>
        <button nz-button
                nzType="primary"
                (click)="copyAiContextToClipboard()"
                [disabled]="isAiContextLoading()"
                class="bg-primary text-primary-button-text hover:bg-hover-primary border-none shadow-sm flex items-center gap-2 font-bold transition-all">
          <i class="fas"
             [ngClass]="isCopied() ? 'fa-check text-success' : 'fa-copy'"></i>
          <span>{{ isCopied() ? config.texts.aiContextModalCopied : config.texts.aiContextModalCopy }}</span>
        </button>
      </div>
    </ng-template>
  </nz-modal>

</div>